import React from 'react';

import { DataTableProps } from './DataTable';
import Pager from './Pager';

export interface WithPagingState<V> {
    page: number;
    dataPage?: V[];
}

export interface WithPagingProps {
    page?: number;
    resetPageOnDataChanged?: boolean;
    renderPager?: boolean;
    updatePage?: (page: number) => void;
}

export default <P extends DataTableProps<V>, V>(Component: React.ComponentType<P>, pageSize: number = 20) =>
    class WithPaging extends React.Component<P & WithPagingProps, WithPagingState<V>> {
        ref: React.RefObject<HTMLDivElement>;

        constructor(props: Readonly<P & WithPagingProps>) {
            super(props);

            this.ref = React.createRef();

            this.state = {
                page: (props.page as number | undefined) || 1
            };
        }

        componentDidMount() {
            this.setState({
                dataPage: this.getDataPage(this.state.page)
            });
        }

        componentDidUpdate(oldProps: P & WithPagingProps, oldState: WithPagingState<V>) {
            if (oldProps.data !== this.props.data) {
                if (this.props.resetPageOnDataChanged !== false) {
                    this.setState({
                        page: 1
                    });

                    this.setState({
                        dataPage: this.getDataPage(1)
                    });
                } else {
                    this.setState({
                        dataPage: this.getDataPage(this.state.page)
                    });
                }
            } else if (this.props.page && oldProps.page !== this.props.page) {
                this.setState({
                    page: this.props.page as number
                });

                if (this.props.updatePage) {
                    this.setState({
                        dataPage: this.getDataPage(this.props.page as number)
                    });
                }
            }

            if (this.state.page !== oldState.page) {
                if (this.props.updatePage) {
                    this.props.updatePage(this.state.page);
                } else {
                    this.setState({
                        dataPage: this.getDataPage(this.state.page)
                    });
                }
            }
        }

        render() {
            const { data, page, resetPageOnDataChanged, renderPager, updatePage, ...rest } = this.props;

            return <div ref={this.ref}>
                <Component data={this.state.dataPage} {...rest as any}>{this.props.children}</Component>
                {data && data.length && !(this.props.renderPager === false) ? <Pager page={this.state.page} pageSize={pageSize} recordCount={data.length} onPageChange={this.onPageChange.bind(this)} className="mt-2" /> : undefined}
            </div>;
        }

        onPageChange(page: number) {
            this.setState({
                page: page
            });
        }

        getDataPage(page: number): V[] | undefined {
            if (!this.props.data) {
                return;
            }

            const start = (page - 1) * pageSize;
            const end = Math.min(this.props.data.length, start + pageSize);
            const result = [];

            for (let i = start; i < end; i++) {
                result.push(this.props.data[i]);
            }

            return result;
        }

        getPageCount(): number {
            if (!this.props.data) {
                return 0;
            }

            return Math.ceil(this.props.data.length / pageSize);
        }
    }